import { useEffect } from "react";
import { useDynamicConfig, useGateValue, useLayer } from "@statsig/react-bindings";
import { useIntl } from "react-intl";
import { Button } from "@oai/platform/ui/Button";
import { X } from "@oai/platform/ui/Icon";

import { useCookieConsent } from "@/hooks/useCookieConsent";
import { useCookieLocation } from "@/hooks/useCookieLocation";
import {
  ADS_COOKIE_BANNER_ROLLOUT_CONFIG,
  COOKIE_BANNER_LAYER,
  COOKIE_PERSONALIZATION_CHOICES_ENABLED_GATE,
  DEFAULT_COOKIE_BANNER_ROLLOUT_COUNTRIES,
  isCookieBannerRolloutCountry,
  requiresCookieBanner,
  resolveCookieBannerRolloutCountries,
  shouldShowCookieBanner,
} from "@/lib/cookieConsent";
import { messages } from "@/localization/Shared.messages";

import type { ReactNode } from "react";

export function CookieBanner({ countryCode }: { countryCode?: string }) {
  const { countryCode: locationCountryCode, regionCode } = useCookieLocation();
  const {
    analyticsConsent,
    marketingConsent,
    personalizationConsent,
    isConsentRequired,
    isConsentResolved,
    isAuthenticated,
    isCookieBannerDismissed,
    dismissCookieBanner,
    setLocalConsentRequired,
    setCookiePreferencesOpen,
    updateAllConsent,
  } = useCookieConsent();
  const resolvedCountryCode = locationCountryCode ?? countryCode;
  const loggedOutConsentRequired = requiresCookieBanner(resolvedCountryCode, regionCode);
  const cookieBannerRolloutConfig = useDynamicConfig(ADS_COOKIE_BANNER_ROLLOUT_CONFIG);
  const configuredRolloutCountries = cookieBannerRolloutConfig.get(
    "countries",
    DEFAULT_COOKIE_BANNER_ROLLOUT_COUNTRIES,
  );
  const rolloutCountries = resolveCookieBannerRolloutCountries(configuredRolloutCountries);
  const includePersonalizationConsent = useGateValue(COOKIE_PERSONALIZATION_CHOICES_ENABLED_GATE);
  const isEligibleForBanner = shouldShowCookieBanner({
    isAuthenticated,
    isBannerDismissed: isCookieBannerDismissed,
    isConsentResolved,
    isConsentRequired: isAuthenticated ? isConsentRequired === true : loggedOutConsentRequired,
    isBannerExperimentCountry: isCookieBannerRolloutCountry(resolvedCountryCode, rolloutCountries),
    analyticsConsent,
    marketingConsent,
    personalizationConsent,
    includePersonalizationConsent,
  });
  const updateBannerConsent = (consent: "agreed" | "rejected") =>
    updateAllConsent(consent, { includePersonalizationConsent });

  useEffect(() => {
    if (!isAuthenticated) {
      setLocalConsentRequired(loggedOutConsentRequired);
    }
  }, [isAuthenticated, loggedOutConsentRequired, setLocalConsentRequired]);

  if (!isEligibleForBanner) {
    return null;
  }

  const banner = (
    <CookieBannerContent
      onAccept={() => updateBannerConsent("agreed")}
      onDismiss={dismissCookieBanner}
      onManage={() => setCookiePreferencesOpen(true)}
      onReject={() => updateBannerConsent("rejected")}
    />
  );

  if (loggedOutConsentRequired) {
    return banner;
  }

  return <CookieBannerSuppressionBoundary>{banner}</CookieBannerSuppressionBoundary>;
}

function CookieBannerSuppressionBoundary({ children }: { children: ReactNode }) {
  const cookieBannerLayer = useLayer(COOKIE_BANNER_LAYER);
  const isBannerSuppressed = cookieBannerLayer.get("suppress_banner", false) === true;

  if (isBannerSuppressed) {
    return null;
  }

  return children;
}

function CookieBannerContent({
  onAccept,
  onDismiss,
  onManage,
  onReject,
}: {
  onAccept: () => void;
  onDismiss: () => void;
  onManage: () => void;
  onReject: () => void;
}) {
  const intl = useIntl();

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
      className="bg-surface fixed inset-x-0 bottom-0 z-[60] border-t border-black/10"
    >
      <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-4 px-6 py-5 md:flex-row md:items-center md:justify-between">
        <div className="flex max-w-[760px] flex-col gap-1.5">
          <p id="cookie-banner-title" className="text-primary text-sm font-medium">
            {intl.formatMessage(messages.cookiesTitle)}
          </p>
          <p id="cookie-banner-description" className="text-secondary text-sm leading-6">
            {intl.formatMessage(messages.cookiesDescription, {
              manageCookies: (chunks) => (
                <button
                  type="button"
                  className="text-primary inline-flex items-center gap-1 align-baseline underline underline-offset-[0.125rem]"
                  onClick={onManage}
                >
                  {chunks}
                </button>
              ),
              cookiePolicy: (chunks) => (
                <a
                  href="https://openai.com/policies/cookie-policy/"
                  className="text-primary underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  {chunks}
                </a>
              ),
            })}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button color="secondary" variant="outline" size="lg" pill onClick={onReject}>
              {intl.formatMessage(messages.rejectNonEssentialCookies)}
            </Button>
            <Button color="primary" variant="solid" size="lg" pill onClick={onAccept}>
              {intl.formatMessage(messages.acceptAllCookies)}
            </Button>
          </div>
          <Button
            color="secondary"
            variant="ghost"
            uniform
            aria-label={intl.formatMessage(messages.dismissCookieBanner)}
            onClick={onDismiss}
          >
            <X />
          </Button>
        </div>
      </div>
    </div>
  );
}
